import React, { useMemo } from 'react'
import { Box, IconButton, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import SupplierPriceCell from './SupplierPriceCell'
import { relativeDate } from './offeringHelpers'

const COLOR_SWATCH = {
  BROWN: '#8B6914',
  GOLD: '#C5A030',
  RED: '#B5412B',
  GRAY: '#808080',
  WHITE: '#D4D4D4',
  BLACK: '#333333',
  TAN: '#C4A87C',
  PINK: '#D4838F',
  GREEN: '#5A8F5A',
  MIXED: '#9B7BB5',
  UNKNOWN: '#B0B0B0'
}

// Canonical column order for pricing modes
const MODE_ORDER = ['PIT_PICKUP', 'YARD_PICKUP', 'RETAIL', 'DELIVERED', 'WHOLESALE', 'RESELLER', 'UNKNOWN']

const MODE_LABELS = {
  PIT_PICKUP: 'PIT',
  YARD_PICKUP: 'YARD',
  RETAIL: 'RETAIL',
  DELIVERED: 'DELIVERED',
  WHOLESALE: 'WHOLESALE',
  RESELLER: 'RESELLER',
  UNKNOWN: 'OTHER'
}

const useStyles = makeStyles(() => ({
  '@keyframes scaleIn': {
    from: { opacity: 0, transform: 'scale(0.97)' },
    to: { opacity: 1, transform: 'scale(1)' }
  },
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-lg)',
    boxShadow: 'var(--shadow-sm)',
    marginBottom: 12,
    overflow: 'hidden',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: 'var(--shadow)'
    },
    '&:hover $actions': {
      opacity: 1
    }
  },
  header: {
    padding: '14px 18px 10px',
    borderBottom: '1px solid var(--line)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  materialName: {
    fontFamily: 'var(--font-ui)',
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--ink)',
    margin: 0,
    lineHeight: 1.3
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 3,
    flexWrap: 'wrap'
  },
  gradationLabel: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--ink-2)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5
  },
  colorDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0
  },
  pill: {
    fontFamily: 'var(--font-mono)',
    fontSize: '10.5px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--ink-3)',
    background: 'var(--bg)',
    borderRadius: 4,
    padding: '1px 6px'
  },
  supplierCount: {
    fontFamily: 'var(--font-mono)',
    fontSize: 12,
    color: 'var(--ink-4)'
  },
  actions: {
    display: 'flex',
    gap: 2,
    opacity: 0,
    transition: 'opacity 0.15s ease'
  },
  actionBtn: {
    color: 'var(--ink-3)',
    padding: 4,
    '&:hover': {
      color: 'var(--primary)'
    }
  },
  gridHeader: {
    background: 'var(--bg)',
    borderBottom: '2px solid var(--line)',
    padding: '6px 18px',
    display: 'grid',
    alignItems: 'center',
    gap: 8
  },
  gridHeaderCell: {
    fontFamily: 'var(--font-mono)',
    fontSize: '10.5px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
    color: 'var(--ink-4)'
  },
  supplierRow: {
    display: 'grid',
    padding: '8px 18px',
    borderBottom: '1px solid var(--line)',
    alignItems: 'center',
    gap: 8,
    transition: 'background 0.15s ease',
    '&:last-child': {
      borderBottom: 0
    },
    '&:hover': {
      background: 'var(--bg)'
    }
  },
  vendorName: {
    fontFamily: 'var(--font-ui)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--ink)'
  },
  locationName: {
    fontFamily: 'var(--font-ui)',
    fontSize: 11,
    color: 'var(--ink-3)',
    lineHeight: 1.2
  },
  stockDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    display: 'inline-block'
  },
  stockIn: { backgroundColor: 'var(--ok)' },
  stockOut: { backgroundColor: 'var(--risk)' },
  stockUnknown: { backgroundColor: 'var(--ink-4)' },
  seen: {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--ink-4)'
  }
}))

export default function MaterialComparisonCard ({ group, onOpenCalc, onOpenCreate }) {
  const classes = useStyles()

  const { materialProfile, gradation, offerings } = group

  // 1. Determine which pricing mode columns to show
  const activeModes = useMemo(() => {
    const modesWithPrice = new Set()
    for (const o of offerings) {
      if (o.price > 0 && o.pricingMode) {
        modesWithPrice.add(o.pricingMode)
      }
    }
    return MODE_ORDER.filter(m => modesWithPrice.has(m))
  }, [offerings])

  // 2. Group offerings by vendor
  const vendorRows = useMemo(() => {
    const byVendor = {}
    for (const o of offerings) {
      const vid = o.vendor?._id || 'unknown'
      if (!byVendor[vid]) {
        byVendor[vid] = {
          vendor: o.vendor || { name: 'Unknown' },
          location: o.location,
          offerings: [],
          bestAvailability: 'UNKNOWN',
          latestSeen: null
        }
      }
      byVendor[vid].offerings.push(o)
      // Track best availability
      if (o.availabilityStatus === 'IN_STOCK') byVendor[vid].bestAvailability = 'IN_STOCK'
      else if (o.availabilityStatus === 'OUT_OF_STOCK' && byVendor[vid].bestAvailability !== 'IN_STOCK') {
        byVendor[vid].bestAvailability = 'OUT_OF_STOCK'
      }
      // Track most recent seen
      if (o.lastSeenAt) {
        if (!byVendor[vid].latestSeen || new Date(o.lastSeenAt) > new Date(byVendor[vid].latestSeen)) {
          byVendor[vid].latestSeen = o.lastSeenAt
        }
      }
    }

    // For each vendor+mode combo, pick the lowest price
    return Object.values(byVendor).map(row => {
      const priceByMode = {}
      for (const o of row.offerings) {
        const mode = o.pricingMode
        if (o.price > 0) {
          if (!priceByMode[mode] || o.price < priceByMode[mode].price) {
            priceByMode[mode] = { price: o.price, unit: o.unit, availabilityStatus: o.availabilityStatus }
          }
        }
      }
      return { ...row, priceByMode }
    })
  }, [offerings])

  // 3. Find absolute best price per column
  const bestPriceByMode = useMemo(() => {
    const best = {}
    for (const row of vendorRows) {
      for (const mode of activeModes) {
        const entry = row.priceByMode[mode]
        if (entry && entry.price > 0) {
          if (!best[mode] || entry.price < best[mode]) {
            best[mode] = entry.price
          }
        }
      }
    }
    return best
  }, [vendorRows, activeModes])

  // Grid template: supplier column + mode columns + stock + seen
  const gridCols = `minmax(120px, 1.2fr) ${activeModes.map(() => 'minmax(90px, 1fr)').join(' ')} 40px 50px`

  const colorFamily = materialProfile?.colorFamily || 'UNKNOWN'
  const swatchColor = COLOR_SWATCH[colorFamily] || COLOR_SWATCH.UNKNOWN

  return (
    <div className={classes.card}>
      {/* Card Header */}
      <div className={classes.header}>
        <div>
          <p className={classes.materialName}>
            {materialProfile?.displayName || 'Unknown Material'}
          </p>
          <div className={classes.meta}>
            {gradation && (
              <span className={classes.gradationLabel}>
                <span className={classes.colorDot} style={{ backgroundColor: swatchColor }} />
                {gradation.displayName}
              </span>
            )}
            {materialProfile?.materialCategory && (
              <span className={classes.pill}>{materialProfile.materialCategory.replace(/_/g, ' ')}</span>
            )}
            {!gradation && colorFamily !== 'UNKNOWN' && (
              <span className={classes.pill} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span className={classes.colorDot} style={{ backgroundColor: swatchColor }} />
                {colorFamily}
              </span>
            )}
          </div>
        </div>
        <Box display="flex" alignItems="center" gap={1}>
          <span className={classes.supplierCount}>
            {group.supplierCount || vendorRows.length} supplier{(group.supplierCount || vendorRows.length) !== 1 ? 's' : ''}
          </span>
          <div className={classes.actions}>
            <Tooltip title="Calculate fulfillment">
              <IconButton
                className={classes.actionBtn}
                size="small"
                onClick={() => onOpenCalc(materialProfile?._id, gradation?._id)}
              >
                <LocalShippingIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add offering for this material">
              <IconButton
                className={classes.actionBtn}
                size="small"
                onClick={() => onOpenCreate(materialProfile?._id, gradation?._id)}
              >
                <AddIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </div>
        </Box>
      </div>

      {/* Grid Header */}
      {activeModes.length > 0 && (
        <>
          <div className={classes.gridHeader} style={{ gridTemplateColumns: gridCols }}>
            <span className={classes.gridHeaderCell}>SUPPLIER</span>
            {activeModes.map(mode => (
              <span key={mode} className={classes.gridHeaderCell}>{MODE_LABELS[mode] || mode}</span>
            ))}
            <span className={classes.gridHeaderCell}>STOCK</span>
            <span className={classes.gridHeaderCell}>SEEN</span>
          </div>

          {/* Supplier Rows */}
          {vendorRows.map((row, idx) => {
            const stockClass = row.bestAvailability === 'IN_STOCK'
              ? classes.stockIn
              : row.bestAvailability === 'OUT_OF_STOCK'
                ? classes.stockOut
                : classes.stockUnknown

            return (
              <div key={row.vendor?._id || idx} className={classes.supplierRow} style={{ gridTemplateColumns: gridCols }}>
                <div>
                  <div className={classes.vendorName}>{row.vendor?.name || 'Unknown'}</div>
                  {row.location?.name && (
                    <div className={classes.locationName}>{row.location.name}</div>
                  )}
                </div>
                {activeModes.map(mode => {
                  const entry = row.priceByMode[mode]
                  const isBest = entry && entry.price === bestPriceByMode[mode]
                  return (
                    <SupplierPriceCell
                      key={mode}
                      price={entry?.price}
                      unit={entry?.unit}
                      isBest={isBest}
                      availabilityStatus={entry?.availabilityStatus || row.bestAvailability}
                    />
                  )
                })}
                <span className={`${classes.stockDot} ${stockClass}`} />
                <span className={classes.seen}>{relativeDate(row.latestSeen)}</span>
              </div>
            )
          })}
        </>
      )}

      {/* Fallback when no priced modes */}
      {activeModes.length === 0 && vendorRows.length > 0 && (
        <Box px={2.25} py={1.5}>
          {vendorRows.map((row, idx) => (
            <Box key={row.vendor?._id || idx} display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
              <span className={classes.vendorName}>{row.vendor?.name || 'Unknown'}</span>
              <span className={classes.seen}>No prices</span>
            </Box>
          ))}
        </Box>
      )}
    </div>
  )
}
